import { api } from "@/api/client";

export type AIProvider = "OLLAMA" | "OPENAI";
export type AuthType = "NONE" | "BEARER" | "API_KEY" | "BASIC";

export interface AIConnection {
  id: string;
  name: string;
  provider: AIProvider;
  base_url: string;
  auth_type: AuthType;
  api_key?: string | null;
  username?: string | null;
  password?: string | null;
  headers?: Record<string, string> | null;
  is_active: boolean;
  enabled_models?: string[];
  is_default_generate?: boolean;
  is_default_embed?: boolean;
  default_gen_model?: string | null;
  default_embed_model?: string | null;
  created_at: string;
  updated_at: string;
}

export type AIDefaultRole = "generate" | "embed";

export interface AIDefaultEntry {
  connection_id?: string | null;
  connection_name?: string | null;
  provider?: string | null;
  base_url?: string | null;
  model?: string | null;
  source: "db" | "none";
}

export interface AIDefaultsResponse {
  generate: AIDefaultEntry;
  embed: AIDefaultEntry;
}

export interface AIConnectionInput {
  name: string;
  provider: AIProvider;
  base_url: string;
  auth_type: AuthType;
  api_key?: string | null;
  username?: string | null;
  password?: string | null;
  headers?: Record<string, string> | null;
  is_active: boolean;
}

export interface AITestResponse {
  ok: boolean;
  status_code?: number | null;
  message: string;
  detail?: string | null;
}

const BASE = "/settings/ai-connections";

export const listAIConnections = () => api.get<AIConnection[]>(`${BASE}/`);
export const createAIConnection = (data: AIConnectionInput) =>
  api.post<AIConnection>(`${BASE}/`, data);
export const updateAIConnection = (id: string, data: AIConnectionInput) =>
  api.put<AIConnection>(`${BASE}/${id}`, data);
export const deleteAIConnection = (id: string) => api.delete<void>(`${BASE}/${id}`);
export const testAIConnection = (data: Omit<AIConnectionInput, "name" | "is_active">) =>
  api.post<AITestResponse>(`${BASE}/test`, data);

export interface AIModelInfo {
  id: string;
  enabled: boolean;
}

export interface AIModelsResponse {
  connection_id: string;
  provider: AIProvider;
  models: AIModelInfo[];
}

export interface EnabledModelEntry {
  connection_id: string;
  connection_name: string;
  provider: AIProvider;
  model: string;
}

export const listConnectionModels = (id: string) =>
  api.get<AIModelsResponse>(`${BASE}/${id}/models`);

export const updateEnabledModels = (id: string, enabled_models: string[]) =>
  api.put<AIConnection>(`${BASE}/${id}/models`, { enabled_models });

export const listAllEnabledModels = () =>
  api.get<EnabledModelEntry[]>(`${BASE}/enabled-models`);

export const getAIDefaults = () =>
  api.get<AIDefaultsResponse>(`${BASE}/defaults`);

export const setAIDefault = (
  id: string,
  role: AIDefaultRole,
  model: string,
) => api.put<AIConnection>(`${BASE}/${id}/set-default`, { role, model });

export const clearAIDefault = (role: AIDefaultRole) =>
  api.delete<void>(`${BASE}/defaults/${role}`);

export interface ChatMessageIn {
  role: "system" | "user" | "assistant";
  content: string;
}

export interface ChatSourceRef {
  doc_id: string;
  judul?: string | null;
  chunk_index: number;
  excerpt: string;
}

export interface ChatResponse {
  content: string;
  model: string;
  provider: AIProvider;
  sources: ChatSourceRef[];
}

export const chatWithConnection = (
  connection_id: string,
  model: string,
  messages: ChatMessageIn[],
  use_documents: boolean = false,
  doc_ids?: string[],
) =>
  api.post<ChatResponse>(`${BASE}/chat`, {
    connection_id,
    model,
    messages,
    use_documents,
    doc_ids: doc_ids && doc_ids.length > 0 ? doc_ids : undefined,
  });
